/**
 * groupVisitsForPrint — pure helper that sections a mixed-queue visit list
 * for the "All"/OPD queue print (A4 + thermal), per Issue-3 (owner ruling:
 * Doctors → Unassigned OPD → OTC → Lab; grand footer only, no
 * per-section subtotals — see issue-3-queue-segregation/build-plan.md),
 * F5 (owner ruling, hierarchical section headers — see
 * bugfix-jul31/phase-otc-procedures scope.md F5): the doctor buckets are no
 * longer bare top-level headers — they nest as SUB-headers inside a single
 * top-level "OPD" group, sibling to the "OTC Visits" / "Lab Visits"
 * top-level groups; and F8 (owner ruling, scope.md F8): the OTC group gains
 * the same per-ATTENDEE sub-header hierarchy — an OTC visit is attended by
 * exactly one of a nurse or a doctor (W3 XOR), so its sub-header is that
 * attendee's name (nurse_member_id/nurse_name, or failing that
 * doctor_member_id/doctor_name for a doctor-attended OTC visit), sorted
 * alphabetically alongside each other exactly like the OPD doctor
 * sub-headers.
 *
 * Category is derived via the single-source `providerCategoryOf` helper
 * (`constants/visitProviderCategories.js`) — never re-derived here. The
 * "OPD" group title is likewise read from `providerCategory('OPD').label`
 * (single source, same PROVIDER_CATEGORIES entry that drives the OPD
 * filter chip) rather than a hardcoded literal.
 *
 * Returns an ORDERED array of top-level GROUPS:
 *   { kind: 'OPD' | 'OTC' | 'LAB' | 'RAD', title, sections }
 *
 * `sections` is an ORDERED array of `{ key, kind, title, rows }`:
 *   - OPD group: one `{ kind: 'DOCTOR', title: <doctor name/id> }` section
 *     per named doctor (OPD rows with a `doctor_member_id`, or failing that
 *     a `doctor_name`), sorted alphabetically by `title` (tie-break by
 *     `doctor_member_id` — two doctors sharing a display name still get two
 *     deterministic sections), followed by AT MOST one
 *     `{ kind: 'UNASSIGNED', title: 'Unassigned' }` section for OPD rows
 *     with neither field (empty-set rule: stays visible whenever present,
 *     never merged into a named doctor's bucket, never alpha-sorted into
 *     the doctor list) — always LAST inside the OPD group.
 *   - OTC group (F8): one section per named ATTENDEE — `{ kind: 'NURSE',
 *     title: <nurse name/id> }` for a nurse-attended row (nurse_member_id /
 *     nurse_name) or `{ kind: 'DOCTOR', title: <doctor name/id> }` for a
 *     doctor-attended row (doctor_member_id / doctor_name, the W3 XOR
 *     bucket) — combined and sorted alphabetically by `title` exactly like
 *     the OPD doctor sections (tie-break by id), followed by AT MOST one
 *     trailing `{ kind: 'UNASSIGNED', title: 'Unassigned' }` section for
 *     OTC rows with neither field (same empty-set rule as OPD). BACK-COMPAT:
 *     when NOT ONE OTC row carries any attendee field (e.g. old rows from
 *     before per-attendee sub-grouping existed), the group falls back to
 *     its original flat single section with `title: null` (no sub-header,
 *     no synthetic "Unassigned" label) — unchanged from pre-F8 output.
 *   - LAB and RAD groups (BUSY-VOUCHER R1, OR-41): the old "Lab Visits"
 *     group is replaced by a "Lab" group then a "Radiology" group. The
 *     section of a test is decided SERVER-side by its Type (print-summary
 *     `lab_sections`, one slice per section — ECG prints under Lab; this file
 *     never reads the frontend IMAGING_TYPES). Each LAB-category visit is
 *     emitted once per slice as `{ ...visit, _printSection, _printSlice }`, so
 *     a visit with both a blood test and an X-ray appears in BOTH groups,
 *     each row carrying only its own tests/amount (the renderer reads the
 *     slice through `rowSummary`). A visit with no `lab_sections` (old
 *     backend) goes whole into Lab. An empty group is skipped.
 *   - Inside each of those groups (lab-settle-daybucket, C1
 *     `is_ipd_lab`/`source`): when at
 *     least one row is IPD-sourced, the group splits into an
 *     `{ kind: 'IPD', title: 'IPD' }` section followed by (when any remain)
 *     an `{ kind: 'OPD', title: 'OPD' }` section — an IPD-only day renders
 *     just the IPD sub-header (same always-label/skip-empty rule as OPD's
 *     Unassigned). BACK-COMPAT: when NO row in the group is IPD-sourced
 *     (regular OPD lab visits, the pre-existing case), the group stays the
 *     original flat single section with `title: null` — byte-unchanged.
 *
 * Group order: OPD (if any doctor/unassigned rows) → OTC → LAB → RAD. An empty
 * group (no rows in that category) is skipped entirely — this includes the
 * OPD group when there are zero OPD rows (e.g. an OPD-filtered print always
 * yields exactly one group; an OTC-only day yields just the OTC group).
 *
 * Rows within a section are sorted ascending by `queue_number`, reusing the
 * same comparator the caller already applies before printing. Duplicate
 * queue_numbers ACROSS sections/groups (and, per F8, ACROSS OTC attendee
 * sub-groups — numbering is per-attendee server-side) are expected — every
 * row is retained.
 *
 * No DOM, no HTML escaping — renderers own presentation/escaping.
 */
import { providerCategoryOf, providerCategory } from '../constants/visitProviderCategories';

const byQueueNumber = (a, b) => (Number(a?.queue_number) || 0) - (Number(b?.queue_number) || 0);
const UNASSIGNED_TITLE = 'Unassigned';

// Lab / Radiology sheet sections (OR-41). `kind` doubles as the server's
// section wire code ('LAB' | 'RAD', print-summary `lab_sections[].section`);
// one title constant each, read by BOTH renderers (A4 `.section-hdr`, thermal
// capitalised heading) via `group.title`.
const LAB_GROUP_TITLE = 'Lab';
const RAD_GROUP_TITLE = 'Radiology';
const RAD_SECTION = 'RAD';
// Group kinds that render with the LAB column set / "Advised by" line.
export const LAB_GROUP_KINDS = ['LAB', RAD_SECTION];

// Alphabetical-by-title (tie-break by id) sort shared by the OPD doctor
// sections and the F8 OTC attendee sections — same comparator, same
// determinism guarantee (two same-named attendees with different ids still
// get two stable sections).
function sortBucketsAlphabetically(buckets) {
  return [...buckets.entries()].sort(([, a], [, b]) => {
    const byTitle = a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
    return byTitle !== 0 ? byTitle : a.id.localeCompare(b.id);
  });
}

// F8: one section per OTC attendee (nurse OR doctor, W3 XOR), alphabetical,
// plus a trailing Unassigned section for rows with neither field — falling
// back to the pre-F8 flat single section when NO row in the group carries
// any attendee field at all (back-compat for old rows).
function otcSections(nurseGroupRows) {
  const buckets = new Map(); // bucketKey -> { title, id, kind, rows: [] }
  const unassignedRows = [];

  nurseGroupRows.forEach((visit) => {
    const nurseId = visit?.nurse_member_id || '';
    const nurseName = visit?.nurse_name || '';
    if (nurseId || nurseName) {
      const bucketKey = `NURSE:${nurseId || `name:${nurseName}`}`;
      if (!buckets.has(bucketKey)) {
        buckets.set(bucketKey, {
          title: nurseName || nurseId, id: nurseId, kind: 'NURSE', rows: [],
        });
      }
      buckets.get(bucketKey).rows.push(visit);
      return;
    }

    const doctorId = visit?.doctor_member_id || '';
    const doctorName = visit?.doctor_name || '';
    if (doctorId || doctorName) {
      const bucketKey = `DOCTOR:${doctorId || `name:${doctorName}`}`;
      if (!buckets.has(bucketKey)) {
        buckets.set(bucketKey, {
          title: doctorName || doctorId, id: doctorId, kind: 'DOCTOR', rows: [],
        });
      }
      buckets.get(bucketKey).rows.push(visit);
      return;
    }

    unassignedRows.push(visit);
  });

  if (buckets.size === 0) {
    // No row in the group carries any attendee field — pre-F8 flat output.
    return [{ key: 'NURSE', kind: 'NURSE', title: null, rows: [...nurseGroupRows].sort(byQueueNumber) }];
  }

  const sections = sortBucketsAlphabetically(buckets).map(([bucketKey, bucket]) => ({
    key: `OTC:${bucketKey}`,
    kind: bucket.kind,
    title: bucket.title,
    rows: [...bucket.rows].sort(byQueueNumber),
  }));

  if (unassignedRows.length > 0) {
    sections.push({
      key: 'OTC:UNASSIGNED',
      kind: 'UNASSIGNED',
      title: UNASSIGNED_TITLE,
      rows: [...unassignedRows].sort(byQueueNumber),
    });
  }

  return sections;
}

// lab-settle-daybucket (C1): split a Lab/Radiology group into an IPD
// sub-section then an OPD sub-section, off the `is_ipd_lab`/`source`
// discriminator — same shape/skip-empty convention as the OPD/OTC
// sub-headers above. `kind` is the group's own kind ('LAB' | 'RAD').
// BACK-COMPAT: a day with zero IPD-sourced rows (the pre-existing case,
// including every fixture that predates `is_ipd_lab`) returns the original
// flat single untitled section, byte-identical to before.
function labSections(groupRows, kind) {
  const ipdRows = [];
  const opdRows = [];

  groupRows.forEach((visit) => {
    const isIpd = visit?.is_ipd_lab === true || visit?.source === 'IPD';
    (isIpd ? ipdRows : opdRows).push(visit);
  });

  if (ipdRows.length === 0) {
    return [{ key: kind, kind, title: null, rows: [...groupRows].sort(byQueueNumber) }];
  }

  const sections = [
    { key: `${kind}:IPD`, kind: 'IPD', title: 'IPD', rows: [...ipdRows].sort(byQueueNumber) },
  ];
  if (opdRows.length > 0) {
    sections.push({ key: `${kind}:OPD`, kind: 'OPD', title: 'OPD', rows: [...opdRows].sort(byQueueNumber) });
  }
  return sections;
}

// Emit a LAB-category visit once per server slice (`summaryMap[id].lab_sections`)
// into the Lab or Radiology bucket. No slices (old backend) → the whole visit,
// untouched, into Lab.
function emitLabVisit(visit, summaryMap, labRows, radRows) {
  const slices = summaryMap?.[visit?.visit_id]?.lab_sections;
  if (!Array.isArray(slices) || slices.length === 0) {
    labRows.push(visit);
    return;
  }
  slices.forEach((slice) => {
    const section = slice?.section === RAD_SECTION ? RAD_SECTION : 'LAB';
    (section === RAD_SECTION ? radRows : labRows).push({
      ...visit, _printSection: section, _printSlice: slice,
    });
  });
}

export function groupVisitsForPrint(rows, summaryMap) {
  const visitRows = Array.isArray(rows) ? rows : [];

  const doctorBuckets = new Map(); // groupKey -> { title, id, rows: [] }
  const unassignedRows = [];
  const nurseRows = [];
  const labRows = [];
  const radRows = [];

  visitRows.forEach((visit) => {
    const category = providerCategoryOf(visit);
    if (category === 'NURSE') {
      nurseRows.push(visit);
      return;
    }
    if (category === 'LAB') {
      emitLabVisit(visit, summaryMap, labRows, radRows);
      return;
    }

    // OPD (including the doctor-led fallback bucket).
    const memberId = visit?.doctor_member_id || '';
    const name = visit?.doctor_name || '';
    if (!memberId && !name) {
      unassignedRows.push(visit);
      return;
    }
    const groupKey = memberId || `name:${name}`;
    if (!doctorBuckets.has(groupKey)) {
      doctorBuckets.set(groupKey, { title: name || memberId, id: memberId, rows: [] });
    }
    doctorBuckets.get(groupKey).rows.push(visit);
  });

  const doctorSections = sortBucketsAlphabetically(doctorBuckets)
    .map(([groupKey, bucket]) => ({
      key: `DOCTOR:${groupKey}`,
      kind: 'DOCTOR',
      title: bucket.title,
      rows: [...bucket.rows].sort(byQueueNumber),
    }));

  const opdSections = [...doctorSections];
  if (unassignedRows.length > 0) {
    opdSections.push({
      key: 'OPD:UNASSIGNED',
      kind: 'UNASSIGNED',
      title: UNASSIGNED_TITLE,
      rows: [...unassignedRows].sort(byQueueNumber),
    });
  }

  const groups = [];

  if (opdSections.length > 0) {
    groups.push({ kind: 'OPD', title: providerCategory('OPD')?.label || 'OPD', sections: opdSections });
  }
  if (nurseRows.length > 0) {
    const otcTitle = `${providerCategory('NURSE')?.shortLabel || 'OTC'} Visits`;
    groups.push({ kind: 'OTC', title: otcTitle, sections: otcSections(nurseRows) });
  }
  if (labRows.length > 0) {
    groups.push({ kind: 'LAB', title: LAB_GROUP_TITLE, sections: labSections(labRows, 'LAB') });
  }
  if (radRows.length > 0) {
    groups.push({ kind: RAD_SECTION, title: RAD_GROUP_TITLE, sections: labSections(radRows, RAD_SECTION) });
  }

  return groups;
}
